import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from './api';

export interface FieldDescriptor {
  field: string;
  label: string;
}

export interface UploadResponse {
  upload_id: string;
  filename: string;
  columns: string[];
  total_rows: number;
  sample_rows: Array<Record<string, unknown>>;
  auto_detected_mapping: Record<string, string>;
  supported_fields: FieldDescriptor[];
}

export interface RowOutcome {
  row_index: number;
  part_number: string | null;
  mfg_pn: string | null;
  action: 'create' | 'skip-duplicate' | 'skip-empty' | 'error';
  message: string | null;
}

/** Three modes for vendor enrichment during import. See the matching
 * Pydantic schema for semantics — kept in sync by the closed-set
 * validation on the server's commit endpoint. */
export type VendorPolicy =
  | 'spreadsheet_wins'
  | 'vendor_fills_blanks'
  | 'vendor_wins';

export interface VendorEnrichmentSummary {
  enriched: number;
  not_found: number;
  rate_limited: number;
  transient_failures: number;
  no_adapter: number;
  skipped_no_pn: number;
  skipped_credentials: number;
  errors: string[];
}

export interface ImportCommitResponse {
  dry_run: boolean;
  created: number;
  skipped_duplicate: number;
  skipped_empty: number;
  errored: number;
  outcomes: RowOutcome[];
  unmapped_categories: string[];
  /** Populated only when the request used a non-default vendor_policy.
   * Null in the (default) `spreadsheet_wins` mode. */
  vendor_enrichment: VendorEnrichmentSummary | null;
}

export interface ImportCommitInput {
  upload_id: string;
  column_mapping: Record<string, string>;
  skip_duplicates: boolean;
  dry_run: boolean;
  vendor_policy?: VendorPolicy;
}

async function uploadSpreadsheet(
  orgSlug: string,
  file: File,
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`/api/orgs/${orgSlug}/parts/import/upload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const text = await response.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!response.ok) {
    const detail =
      typeof body === 'object' && body !== null && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(detail, response.status, body);
  }
  return body as UploadResponse;
}

export function useUploadSpreadsheet(orgSlug: string | undefined) {
  return useMutation({
    mutationFn: (file: File) => {
      if (!orgSlug) throw new Error('no active org');
      return uploadSpreadsheet(orgSlug, file);
    },
  });
}

export function useCommitImport(orgSlug: string | undefined) {
  return useMutation({
    mutationFn: (input: ImportCommitInput) =>
      api.postJson<ImportCommitResponse>(
        `/api/orgs/${orgSlug}/parts/import/commit`,
        input,
      ),
  });
}
